/**
 * postListRegistry.js
 * ───────────────────
 * Keeps one story from appearing in several Post List blocks on the same page.
 *
 * Every Post List joins a group (default: the whole page) in the order it
 * mounts, which is top-to-bottom document order. Before fetching, a block
 * waits for the members ahead of it in the same group to claim their posts,
 * then fetches with those ids excluded (the server does the exclusion, so
 * counts, Load more and Pages stay right). When an earlier block changes its
 * claim later (editor edits, a Related list re-targeting), the blocks behind
 * it are told to refetch.
 *
 * A scope separates pages: the public SPA passes the page id, the editor its
 * own token, so a stale claim from a previous page never leaks forward.
 *
 * Module state, no Vue: both bundles (editor and public renderer) share it.
 */

var seq = 0;
/** scope → group → [{ token, seq, ids: Set|null, done: bool }] */
var groups = {};
/** token → { scope, group, waiters: Function[], listeners: Function[] } */
var members = {};
/** A member ahead that never fetches (error, hidden) must not block the page forever. */
var TURN_TIMEOUT_MS = 8000;
/** Extra rows a block asks for up front so it can drop already-shown stories without a second round trip. */
export var OVERFETCH = 10;
export var MAX_FETCH = 50;

/**
 * Applies a claim set to an over-fetched list: drops the shown ids, keeps
 * `limit`. Returns what to show, and whether the buffer ran dry (caller then
 * refetches with server-side exclusion).
 */
export function applyExclusions(items, excludedIds, limit) {
	var skip = {};
	(excludedIds || []).forEach(function (id) { skip[String(id)] = true; });
	var kept = [];
	var dropped = 0;
	(items || []).forEach(function (it) {
		if (it && skip[String(it.websitePostID)]) { dropped++; return; }
		kept.push(it);
	});
	return { items: kept.slice(0, limit), dropped: dropped, short: kept.length < limit && (items || []).length >= limit };
}

function list(scope, group) {
	if (!groups[scope]) groups[scope] = {};
	if (!groups[scope][group]) groups[scope][group] = [];
	return groups[scope][group];
}

function membersAhead(member) {
	var l = list(member.scope, member.group);
	var out = [];
	for (var i = 0; i < l.length; i++) {
		if (l[i].token === member.token) break;
		out.push(l[i]);
	}
	return out;
}

function excludedFor(member) {
	var ids = [];
	membersAhead(member).forEach(function (m) { if (m.ids) m.ids.forEach(function (id) { ids.push(id); }); });
	return ids;
}

function notifyBehind(member) {
	var l = list(member.scope, member.group);
	var after = false;
	l.forEach(function (m) {
		if (m.token === member.token) { after = true; return; }
		if (after) (members[m.token].listeners || []).forEach(function (fn) { try { fn(); } catch (e) { /* listener error must not break the chain */ } });
	});
}

function releaseWaiters(member) {
	var l = list(member.scope, member.group);
	l.forEach(function (m) {
		var mm = members[m.token];
		if (!mm || !mm.waiters.length) return;
		var ahead = membersAhead(mm);
		var ready = ahead.every(function (a) { return a.done; });
		if (!ready) return;
		var w = mm.waiters; mm.waiters = [];
		w.forEach(function (fn) { fn(); });
	});
}

var registryApi = {
	/**
	 * @param {string} scope page identity
	 * @param {string} group '' means the page-wide group
	 * @returns {string} member token
	 */
	join: function (scope, group) {
		var token = 'pl' + (++seq);
		var entry = { token: token, seq: seq, ids: null, done: false };
		list(scope, group || 'page').push(entry);
		members[token] = { token: token, scope: scope, group: group || 'page', entry: entry, waiters: [], listeners: [] };
		return token;
	},

	leave: function (token) {
		var m = members[token];
		if (!m) return;
		var l = list(m.scope, m.group);
		var i = l.indexOf(m.entry);
		if (i !== -1) l.splice(i, 1);
		delete members[token];
		// whoever waited on this member can go now
		l.forEach(function (e) { releaseWaiters({ scope: m.scope, group: m.group, token: e.token }); });
	},

	/**
	 * Resolves with the ids to exclude once every member ahead has claimed
	 * (or the wait times out).
	 * @returns {Promise<Array>}
	 */
	waitForTurn: function (token) {
		var m = members[token];
		if (!m) return Promise.resolve([]);
		var ahead = membersAhead(m);
		var hand = function (mm) { var ids = excludedFor(mm); mm.entry.lastExcluded = ids; return ids; };
		if (ahead.every(function (a) { return a.done; })) return Promise.resolve(hand(m));
		return new Promise(function (resolve) {
			var settled = false;
			var finish = function (why) { if (settled) return; settled = true; if (members[token]) members[token].entry.timedOut = why === 'timeout'; resolve(members[token] ? hand(members[token]) : []); };
			m.waiters.push(finish);
			setTimeout(function () { finish('timeout'); }, TURN_TIMEOUT_MS);
		});
	},

	/** Records what this member shows and lets the members behind it proceed (or refetch). */
	claim: function (token, ids) {
		var m = members[token];
		if (!m) return;
		var set = new Set((ids || []).map(function (x) { return String(x); }));
		var changed = !m.entry.done || !sameSet(m.entry.ids, set);
		m.entry.ids = set;
		var wasDone = m.entry.done;
		m.entry.done = true;
		releaseWaiters(m);
		if (wasDone && changed) notifyBehind(m);
	},

	/** Called when a member ahead changed its claim after this member fetched. */
	onChangeAhead: function (token, fn) {
		var m = members[token];
		if (m) m.listeners.push(fn);
	},

	/** Debug view: who is in each group, in order, and what they claimed. */
	inspect: function () {
		var out = {};
		Object.keys(groups).forEach(function (scope) {
			out[scope] = {};
			Object.keys(groups[scope]).forEach(function (g) {
				out[scope][g] = groups[scope][g].map(function (e) { return { token: e.token, done: e.done, ids: e.ids ? Array.from(e.ids) : null, lastExcluded: e.lastExcluded || null, timedOut: !!e.timedOut, waiting: (members[e.token] || { waiters: [] }).waiters.length }; });
			});
		});
		return out;
	},

	/** Tests and page navigation: forget a scope. */
	reset: function (scope) {
		if (scope === undefined) { groups = {}; members = {}; return; }
		Object.keys(groups[scope] || {}).forEach(function (g) {
			groups[scope][g].forEach(function (e) { delete members[e.token]; });
		});
		delete groups[scope];
	}
};

function sameSet(a, b) {
	if (!a || !b || a.size !== b.size) return false;
	var same = true;
	a.forEach(function (v) { if (!b.has(v)) same = false; });
	return same;
}

// Debug handle for the browser console (read-only use).
if (typeof window !== 'undefined') window.__postListRegistry = registryApi;

export default registryApi;
